<script setup lang="ts">
/**
 * **全屏节点选择器** —— 触摸端的「添加节点」。
 *
 * ## 为什么触摸端不能接着用下拉菜单
 *
 * 下拉菜单是**以悬停为中心**的控件：子菜单靠 hover 展开、项高 28px、
 * 靠一屏内的滚动找东西。在触摸端这三条全不成立 ——
 * 没有 hover、手指点不准 28px、而菜单已经有十几个项。
 *
 * 所以粗指针下换一个控件，而不是把下拉菜单改大：
 *
 *   · **常驻搜索框**（顶栏，不随滚动）—— 手机上最快的路径是打两个字；
 *   · **整屏可滚**，分组标题粘顶 —— 浏览时随时知道自己在哪一组；
 *   · **每项高 48px**，够手指点；
 *   · **安全区**留着（刘海 / 手势条）。
 *
 * ⚠️ 内容**和下拉菜单完全同源**（都吃 `addSections`）——
 * 不会出现「快捷方式里有的节点，长按里没有」。
 *
 * ⚠️ **不自动聚焦搜索框**：一进来就弹键盘会盖掉半屏，
 * 而「我就想从最近里点一个」是同样常见的用法。
 * 想搜就点一下输入框 —— 少一次自动弹键盘，多一次点击，这个取舍值。
 */
import { computed, ref, watch } from 'vue'
import { searchMenu } from '@/types/menu'
import type { MenuItem, MenuSection } from '@/types/menu'

const props = defineProps<{
  /** 和下拉菜单同一份内容（分类标题 + 节点项） */
  sections: MenuSection[]
}>()

const emit = defineEmits<{
  (e: 'pick', item: MenuItem): void
  (e: 'close'): void
}>()

const query = ref('')
const searchEl = ref<HTMLInputElement | null>(null)

/** 搜索时拍成一组结果；不搜就按分组显示 */
const shown = computed<MenuSection[]>(() => searchMenu(props.sections, query.value))

const total = computed(() => shown.value.reduce((sum, s) => sum + s.items.length, 0))

/** 打开时把搜索框清掉 —— 上一次搜的词留在那儿会让人以为菜单少了一半 */
watch(
  () => props.sections,
  () => {
    query.value = ''
  },
)

function focusSearch(): void {
  searchEl.value?.focus()
}

/**
 * 选了一项。
 *
 * ⚠️ **必须同时 emit `close`** —— 这一条和 `NpMenu.pick()` 是同一个契约，
 * 而这里**漏了**：只发 `pick` 的话，宿主把节点加进图里，但选择器
 * **一直挂在屏幕上不走**（实测反馈：「添加节点的菜单不会在选择后消失」）。
 *
 * ⚠️ 顺序也不能反：**先 `pick` 后 `close`**。
 * 宿主在 `onMenuSelect` 里会写 `menuPicked = true`，而 `closeMenu` 靠它
 * 区分「选了某一项」和「直接关掉」（后者在连线菜单里等于**断开**）。
 * 反过来的话，每次选一项都会被当成「直接关掉」→ 误触发断开提示。
 */
function pick(item: MenuItem): void {
  if (item.disabled) return
  emit('pick', item)
  emit('close')
}
</script>

<template>
  <div class="np-picker" role="dialog" aria-label="添加节点">
    <!--
      点背景关闭。单独一层而不是把 click 挂在容器上：
      容器自己也吃点击的话，滚动/滑动结束时的一次轻触就会误关。
    -->
    <div class="np-picker__scrim" @click="emit('close')" />

    <div class="np-picker__sheet">
      <header class="np-picker__head">
        <span class="np-picker__title">添加节点</span>
        <button class="np-picker__close" type="button" aria-label="关闭" @click="emit('close')">
          ✕
        </button>
      </header>

      <!-- 常驻搜索框：显式 `type="search"` 才会出「清空」按钮 -->
      <div class="np-picker__search">
        <input
          ref="searchEl"
          v-model="query"
          class="np-picker__input"
          type="search"
          placeholder="搜索节点类型…"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
        />
        <button
          v-if="query"
          class="np-picker__clear"
          type="button"
          aria-label="清空搜索"
          @click="query = ''"
        >
          ✕
        </button>
      </div>

      <div class="np-picker__body">
        <p v-if="total === 0" class="np-picker__empty">
          没有匹配「{{ query }}」的节点。
        </p>

        <!--
          ⚠️ 这里原来挂着一个「分组」修饰类，但**没有任何样式**：
          真正有样式的是下一行 h4 上那个「分组标题」类。
          两个名字只差一个后缀，很容易看混，所以删掉那个没人用的。
          （类名不写在这儿 —— `verify:css` 是按「出现在 class 属性里」判的，
           把名字写进注释会被当成"还在用"。）
        -->
        <section v-for="section in shown" :key="section.id">
          <h4 v-if="section.title" class="np-picker__group-title">{{ section.title }}</h4>
          <button
            v-for="item in section.items"
            :key="item.id"
            class="np-picker__item"
            type="button"
            :disabled="item.disabled"
            @click="pick(item)"
          >
            <span class="np-picker__label">{{ item.label }}</span>
            <!--
              hint 是**类型标识**（「包名.节点名」那种）——
              手机上没地方看悬停提示，直接印出来更有用：
              写文档、跟人沟通时都要用到它。
            -->
            <span v-if="item.hint" class="np-picker__hint">{{ item.hint }}</span>
          </button>
        </section>
      </div>
    </div>
  </div>
</template>

<style>
/*
 * 不用 scoped：这一层被 Teleport 到 body，类名全局唯一（np-picker__），
 * 和 NpMenu 的处理一致。
 *
 * z-index 要**高于菜单**（菜单是 200）：选择器是从菜单里点出来的，
 * 它必须盖在菜单上面。
 */
.np-picker {
  position: fixed;
  inset: 0;
  z-index: 300;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.np-picker__scrim {
  position: absolute;
  inset: 0;
  /* ⚠️ 走令牌，和 `NpInfoDialog` 共用同一个值（原本这里是写死的） */
  background: var(--np-scrim);
}

.np-picker__sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  /*
   * ⚠️ **固定高度**，不是 `max-height`。
   *
   * 用 `max-height` 时高度由内容撑 —— 搜索之后只剩两条结果，整块面板
   * 就"缩"下去了，手指刚抬起来界面又跳一下（实测反馈：
   * 「搜索后节点数量少的话高度会有变化，不是一直全屏」）。
   * 留 28px 是让人看得见底下是画布，不是换了一页（原来的设计意图），
   * 但**这个留白是常量**，不随结果数量变。
   */
  height: calc(100dvh - 28px);
  border-top-left-radius: var(--np-radius-l);
  border-top-right-radius: var(--np-radius-l);
  /*
   * ⚠️ 皮肤走**弹层令牌**。
   *
   * 改动前这里是 `background: var(--np-panel-bg)` —— 那是「面板的**弱底**」
   * （深色下只有 28% 不透明的黑），铺一整屏等于**没有背景**，
   * 底下的画布和节点全透上来，字压在上面根本看不清。
   * 同一批弹层里 `.np-menu` / `.np-color-popover` 早就换成 `--np-popup-*` 了，
   * 只有这个选择器漏掉了（它的样式写在 `.vue` 里，不在那两个 `.css` 里）。
   *
   * ⚠️ 另外 `border-top: 1px solid var(--np-border)` 引用的
   * `--np-border` **根本不存在**（令牌叫 `--np-border-soft` / `-strong` / `-bar`），
   * 所以那条边框一直是**无效声明、从来没画出来过**。
   */
  border-top: 1px solid var(--np-popup-border, var(--np-border-strong));
  background: var(--np-popup-bg, var(--np-surface-popup));
  backdrop-filter: blur(var(--np-popup-blur, 0px));
  box-shadow: var(--np-popup-shadow, var(--np-shadow-popup));
  color: var(--np-panel-text);
  /* 手势条 / 刘海底边留白 */
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.np-picker__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 6px;
}

.np-picker__title {
  flex: 1 1 auto;
  font-size: 13px;
  font-weight: 600;
}

.np-picker__close {
  /* 44px 是触摸命中区的最小尺寸 */
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: var(--np-radius-s);
  background: transparent;
  color: var(--np-panel-mute);
  font-size: 15px;
  cursor: pointer;
}

.np-picker__close:active {
  background: var(--np-panel-chip-bg);
  color: var(--np-panel-text);
}

.np-picker__search {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 12px 8px;
  padding: 0 10px;
  /* `--np-border` / `--np-surface-sunken` 都不存在 —— 换成真实令牌 */
  border: 1px solid var(--np-border-soft);
  border-radius: var(--np-radius-m);
  background: var(--np-panel-input-bg);
}

.np-picker__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 40px;
  border: 0;
  background: transparent;
  color: var(--np-panel-text);
  font: inherit;
  font-size: 14px;
  outline: none;
}

.np-picker__input::placeholder {
  color: var(--np-panel-mute);
}

/* iOS 上 `type=search` 会带一条原生装饰，去掉才和整体一致 */
.np-picker__input::-webkit-search-decoration,
.np-picker__input::-webkit-search-cancel-button {
  appearance: none;
}

.np-picker__clear {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  color: var(--np-panel-mute);
  cursor: pointer;
}

.np-picker__body {
  flex: 1 1 auto;
  overflow-y: auto;
  /* 触摸滚动要顺；`contain` 免得滚到底之后带动外层 */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 0 8px 12px;
}

.np-picker__group-title {
  /* 粘顶：滚动时随时知道在哪一组 */
  position: sticky;
  top: 0;
  z-index: 1;
  margin: 0;
  padding: 8px 8px 4px;
  /*
   * ⚠️ 必须和面板本身**同一个底色** —— 粘顶标题的作用是盖住从下面滚过去的
   * 内容，底色不一致就会在顶上留一条颜色不同的带子，
   * 看着像渲染错位（原来这里是 `--np-panel-bg`，和面板那份一样是错的）。
   */
  background: var(--np-popup-bg, var(--np-surface-popup));
  color: var(--np-panel-mute);
  font-size: 11px;
  font-weight: 600;
}

.np-picker__item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  width: 100%;
  /* 48px：手指够得着 */
  min-height: 48px;
  padding: 10px 10px;
  border: 0;
  border-radius: var(--np-radius-s);
  background: transparent;
  color: var(--np-panel-text);
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.np-picker__item:active {
  background: var(--np-panel-chip-bg);
}

.np-picker__item:disabled {
  opacity: var(--np-opacity-dim);
}

.np-picker__label {
  flex: 0 0 auto;
}

.np-picker__hint {
  flex: 1 1 auto;
  overflow: hidden;
  color: var(--np-panel-mute);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.np-picker__empty {
  margin: 24px 12px;
  color: var(--np-panel-mute);
  font-size: 13px;
  text-align: center;
}
</style>
